/** Reconnect backoff: 1s, 2s, 4s... capped at 30s, for at most 8 retries
 * (about 2.5 minutes) after the last successful open. The cap and count match
 * `usePantryAuth`; the first retry keeps the socket's old 1s delay. */
const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;
const MAX_RETRIES = 8;
/** Focus and online events restart a socket that ran out of retries, at most
 * this often. */
const WAKE_THROTTLE_MS = 5_000;

type MaybeRecord = Record<string, string | number | boolean | undefined>;
type QueryOption = MaybeRecord | (() => MaybeRecord | Promise<MaybeRecord>);

export interface UsePartySocketOptions {
  host: string;
  party: string;
  room: string;
  query?: QueryOption;
  id?: string;
  immediate?: boolean;
  autoReconnect?: boolean;
}

export interface UsePartySocketReturn<T = string> {
  data: Ref<T | null>;
  connected: Ref<boolean>;
  /** Set to the close reason when the server rejects the connection (code 1008). */
  error: Ref<string | null>;
  /** Rejects when the connection query fails (for example the token route).
   * With `autoReconnect`, a rejection also schedules a retry, and `close()`
   * cancels it. */
  open: () => Promise<void>;
  close: () => void;
  /** False when the socket is closed and the message was dropped. */
  send: (value: string) => boolean;
}

function toQueryString(query?: MaybeRecord): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export function usePartySocket<T = string>(
  options: UsePartySocketOptions,
): UsePartySocketReturn<T> {
  const data = shallowRef<T | null>(null);
  const connected = ref(false);
  const error = ref<string | null>(null);

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped by close(). A connect attempt that started earlier sees the
   * change after its await and gives up instead of creating a socket. */
  let generation = 0;
  /** The connect attempt in flight, so concurrent open() calls share it. */
  let opening: Promise<void> | null = null;
  /** Retries since the last socket that opened. */
  let retries = 0;
  /** Set by open() and cleared by close(). Only a socket the caller wants
   * retries or wakes. */
  let wanted = false;
  /** When connect() last ran, to throttle wake. */
  let lastAttemptAt = 0;

  const shouldReconnect = options.autoReconnect ?? false;

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  /** Retries with exponential backoff after a failed query or an unintended
   * close, until the retries run out. Wake starts over after that. */
  function scheduleReconnect() {
    if (
      !shouldReconnect ||
      !wanted ||
      reconnectTimer ||
      error.value ||
      retries >= MAX_RETRIES
    ) {
      return;
    }
    const delay = Math.min(FIRST_RETRY_MS * 2 ** retries, MAX_RETRY_MS);
    retries++;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      // A failure reschedules itself in open(); this catch only keeps the
      // promise from going unhandled.
      open().catch(() => {});
    }, delay);
  }

  async function socketUrl() {
    const protocol =
      options.host.startsWith('localhost') ||
      options.host.startsWith('127.0.0.1')
        ? 'ws'
        : 'wss';

    const resolvedQuery =
      typeof options.query === 'function'
        ? await options.query()
        : options.query;
    const query = toQueryString(resolvedQuery);
    const id = options.id ? `/${encodeURIComponent(options.id)}` : '';
    return `${protocol}://${options.host}/parties/${options.party}/${options.room}${id}${query}`;
  }

  async function connect() {
    clearReconnectTimer();
    lastAttemptAt = Date.now();
    const attempt = generation;
    let url: string;
    try {
      url = await socketUrl();
    } catch (e) {
      // A closed attempt's failure belongs to nobody.
      if (attempt !== generation) return;
      throw e;
    }
    // close() or unmount ran during the await.
    if (attempt !== generation) return;

    const ws = new WebSocket(url);
    socket = ws;

    // Each socket's listeners act only while it is the current socket, so a
    // socket closed by close() or replaced by a retry cannot touch state or
    // schedule a reconnect.
    ws.addEventListener('open', () => {
      if (socket !== ws) return;
      retries = 0;
      connected.value = true;
      error.value = null;
    });

    ws.addEventListener('message', (event) => {
      if (socket !== ws) return;
      data.value = event.data as T;
    });

    ws.addEventListener('close', (event) => {
      if (socket !== ws) return;
      connected.value = false;
      socket = null;
      // 1008 (policy violation) = the server rejected the token; reconnecting
      // won't help until the device is registered, so surface it and stop.
      if (event.code === 1008) {
        error.value = event.reason || 'unauthorized';
        return;
      }
      scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      if (socket !== ws) return;
      connected.value = false;
    });
  }

  function open(): Promise<void> {
    if (import.meta.server) return Promise.resolve();
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      return Promise.resolve();
    }
    if (opening) return opening;
    wanted = true;

    // connect() only rejects for the current generation; a stale attempt
    // returns instead, so a closed socket never schedules a retry here.
    const attempt = connect()
      .catch((e: unknown) => {
        scheduleReconnect();
        throw e;
      })
      .finally(() => {
        if (opening === attempt) opening = null;
      });
    opening = attempt;
    return attempt;
  }

  /** Closes the socket on purpose: nothing reconnects until the next open(). */
  function close() {
    generation++;
    opening = null;
    wanted = false;
    retries = 0;
    clearReconnectTimer();
    const ws = socket;
    socket = null;
    ws?.close();
    connected.value = false;
  }

  function send(value: string) {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(value);
    return true;
  }

  if (options.immediate && import.meta.client) {
    open();
  }

  /** After the retries run out, a focused tab or a restored network starts
   * over with a fresh backoff. */
  function wake() {
    if (
      !wanted ||
      socket ||
      opening ||
      error.value ||
      Date.now() - lastAttemptAt < WAKE_THROTTLE_MS
    ) {
      return;
    }
    clearReconnectTimer();
    retries = 0;
    open().catch(() => {});
  }

  if (shouldReconnect && import.meta.client) {
    onMounted(() => {
      window.addEventListener('focus', wake);
      window.addEventListener('online', wake);
    });
  }

  onBeforeUnmount(() => {
    if (shouldReconnect && import.meta.client) {
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
    }
    close();
  });

  return {
    data,
    connected,
    error,
    open,
    close,
    send,
  };
}
